import {
  Dispatch,
  Fragment,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ChatContextData } from '@eluve/api-contract';

import type { OverlayState } from '../FloatingAction/FloatingActionCard';

export type ChatSeedInitialView = 'new' | 'history';

/** Which entry surface produced the action, for analytics attribution. */
export type ChatEntrySource =
  | 'entry_pill_collapsed'
  | 'entry_pill_expanded'
  | 'entry_pill_input'
  | 'extension_card'
  | 'panel';

export interface ChatSeedState {
  initialMessage?: string;
  initialStarterId?: string;
  initialStarterTitle?: string;
  initialPreferenceId?: string;
  initialSpecTemplateId?: string;
  initialContext?: ChatContextData;
  initialView: ChatSeedInitialView;
  entrySource?: ChatEntrySource;
}

interface ChatSeedActions {
  setSeed: (seed: Partial<ChatSeedState>) => void;
  consumeSeed: () => ChatSeedState;
  setChatState: Dispatch<SetStateAction<OverlayState>>;
  setDraftInput: Dispatch<SetStateAction<string>>;
}

interface ChatOverlayState {
  chatState: OverlayState;
  draftInput: string;
  /** Increments on every `setSeed`, never on a consume. See {@link ChatSeedBoundary}. */
  seedGeneration: number;
}

const EMPTY_SEED: ChatSeedState = { initialView: 'new' };

const ChatSeedActionsContext = createContext<ChatSeedActions | null>(null);
const ChatSeedStateContext = createContext<ChatOverlayState | null>(null);

export const ChatSeedProvider = ({ children }: { children: ReactNode }) => {
  const [seed, setSeedState] = useState<ChatSeedState>(EMPTY_SEED);
  const [chatState, setChatState] = useState<OverlayState>('collapsed');
  const [draftInput, setDraftInput] = useState<string>('');
  const [seedGeneration, setSeedGeneration] = useState(0);

  const seedRef = useRef(seed);
  seedRef.current = seed;

  const setSeed = useCallback((next: Partial<ChatSeedState>) => {
    setSeedState({ ...EMPTY_SEED, ...next });
    setSeedGeneration((generation) => generation + 1);
  }, []);

  /** Consumers read the seed from a `useState` initializer, i.e. during render,
   *  so the reset is deferred — a render-phase setState on this provider is a
   *  React error. The ref is deliberately left intact: StrictMode invokes that
   *  initializer twice and the second call must return the same seed. */
  const consumeSeed = useCallback(() => {
    const current = seedRef.current;
    queueMicrotask(() => setSeedState(EMPTY_SEED));
    return current;
  }, []);

  const actions = useMemo<ChatSeedActions>(
    () => ({ setSeed, consumeSeed, setChatState, setDraftInput }),
    [setSeed, consumeSeed],
  );

  const state = useMemo<ChatOverlayState>(
    () => ({ chatState, draftInput, seedGeneration }),
    [chatState, draftInput, seedGeneration],
  );

  return (
    <ChatSeedActionsContext.Provider value={actions}>
      <ChatSeedStateContext.Provider value={state}>
        {children}
      </ChatSeedStateContext.Provider>
    </ChatSeedActionsContext.Provider>
  );
};

export const useChatSeedActions = (): ChatSeedActions => {
  const ctx = useContext(ChatSeedActionsContext);
  if (!ctx) {
    throw new Error('useChatSeedActions must be used within ChatSeedProvider');
  }
  return ctx;
};

export const useChatSeedState = (): ChatOverlayState => {
  const ctx = useContext(ChatSeedStateContext);
  if (!ctx) {
    throw new Error('useChatSeedState must be used within ChatSeedProvider');
  }
  return ctx;
};

/**
 * Gives its subtree a fresh identity on every seed hand-off, so a consumer that
 * reads the seed from a mount-time initializer gets each one.
 *
 * Wrap the panel, not the surface that seeds it. Panels animate out, and their
 * host keeps them mounted until that finishes — reopening inside that window
 * leaves the old instance in place and its initializer never re-runs.
 */
export const ChatSeedBoundary = ({ children }: { children: ReactNode }) => {
  const { seedGeneration } = useChatSeedState();

  return <Fragment key={seedGeneration}>{children}</Fragment>;
};
